import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCierreSuave, useFocoModal, useVolverCierra, traspasarModal } from '../movimiento.js';
import { useLang } from '../i18n.jsx';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';

/**
 * Aviso de que algo requiere Pro.
 *
 * APARECE SOLO AL PULSAR lo bloqueado, nunca antes. Nada de intersticiales ni de
 * banderines permanentes: quien usa el plan gratis tiene que poder trabajar sin
 * que le recuerden cada dos minutos que no paga. El limite se explica cuando se
 * topa con el, que es el unico momento en que la explicacion sirve de algo.
 *
 * Y se dice el numero concreto -- "llevas 1 de 1 mazos" -- porque "necesitas
 * Pro" no explica nada: no dice que se ha alcanzado, ni cuanto daria el plan.
 */
export default function ProGate({ open, onClose, feature, limit, current }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [pagos, setPagos] = useState(null);
  const [yendo, setYendo] = useState(false);

  /**
   * Se pregunta al servidor si los pagos estan activos, en vez de decidirlo en
   * el cliente. Asi el boton aparece SOLO cuando de verdad puede cobrar: el dia
   * que se configuren las credenciales no hay que tocar el front, y mientras
   * tanto no se enseña un boton que no funciona.
   */
  useEffect(() => {
    if (!open) return;
    api('/api/pagos/estado')
      .then((r) => (r.ok ? r.json() : null))
      .then(setPagos)
      .catch(() => setPagos(null));
  }, [open]);

  const suscribirse = async () => {
    setYendo(true);
    try {
      const r = await api('/api/pagos/suscripcion', { method: 'POST' });
      if (!r.ok) return;
      const { initPoint } = await r.json();
      // Mercado Pago cobra en SU dominio: no se intenta imitar su formulario ni
      // se piden datos de tarjeta aqui.
      if (initPoint) window.location.href = initPoint;
    } finally {
      setYendo(false);
    }
  };

  /*
   * ANTES del `return` condicional, y no despues.
   *
   * Estaba mas abajo, tras el `if (!open) return null`: eso hace que el numero
   * de hooks cambie entre un render y otro -- menos cuando esta cerrado, mas
   * cuando se abre -- y React lo rechaza con "Rendered more hooks than during
   * the previous render". Lo introduje yo al añadir el cierre suave.
   */
  const { ref, cerrando, pedirCierre } = useCierreSuave(onClose, open);
  useFocoModal(ref, open);
  useVolverCierra(open, pedirCierre);

  if (!open) return null;

  const textos = {
    decks: { titulo: t('gateDecksTitle'), cuerpo: t('gateDecksBody', { limit: limit ?? 1 }) },
    matrix: { titulo: t('gateMatrixTitle'), cuerpo: t('gateMatrixBody') },
    meta: { titulo: t('gateMetaTitle'), cuerpo: t('gateMetaBody') },
    matches: { titulo: t('gateMatchesTitle'), cuerpo: t('gateMatchesBody', { limit: limit ?? 20 }) },
    metaExport: { titulo: t('gateMetaExportTitle'), cuerpo: t('gateMetaExportBody') },
    deckStats: { titulo: t('gateDeckStatsTitle'), cuerpo: t('gateDeckStatsBody') },
    community: { titulo: t('gateCommunityTitle'), cuerpo: t('gateCommunityBody') },
    analista: { titulo: t('gateAnalistaTitle'), cuerpo: t('gateAnalistaBody') },
  };
  const { titulo, cuerpo } = textos[feature] ?? { titulo: t('gateGenericTitle'), cuerpo: '' };

  return createPortal(
    <div className={`overlay overlay--acc${cerrando ? ' is-closing' : ''}`} onClick={pedirCierre}>
      <div ref={ref} className="acc progate" role="dialog" aria-modal="true"
           onClick={(e) => e.stopPropagation()}>
        <header className="acc__head">
          <h2>{titulo}</h2>
          <button type="button" className="detail__close" onClick={pedirCierre} aria-label={t('close')}>
            ×
          </button>
        </header>

        <p className="progate__body">{cuerpo}</p>
        {current != null && limit != null && Number.isFinite(limit) && (
          <p className="progate__count">{t('gateUsage', { current, limit })}</p>
        )}

        <ul className="progate__list">
          <li>{t('proDecks')}</li>
          <li>{t('proMeta')}</li>
          <li>{t('proDeckStats')}</li>
          <li>{t('proAnalista')}</li>
          <li>{t('proHistory')}</li>
          <li>{t('proTracker')}</li>
        </ul>

        {/*
          SIN SESION la llamada a /api/pagos/estado devuelve 401 y aqui salia el
          texto de "los pagos no estan activos" -- falso e incoherente: los
          pagos estan activos, lo que falta es la cuenta. A quien no ha entrado
          se le ofrece el camino real: crear cuenta (que ademas estrena 3 dias
          de Pro al confirmar el correo) y desde ahi pagar si quiere.
        */}
        {!user ? (
          <button
            type="button"
            className="btn acc__wide"
            onClick={() => {
              // El panel de cuenta lo abre App; un evento evita pasar la
              // funcion por todos los sitios que abren un ProGate. El traspaso
              // (y no pedirCierre + evento) evita que el back() pendiente de
              // este modal cierre el panel recien abierto. Y se abre en modo
              // REGISTRO: el boton promete crear cuenta, no un formulario de
              // entrar con un enlace mas que buscar.
              traspasarModal(() =>
                window.dispatchEvent(
                  new CustomEvent('ptcg:abrir-cuenta', { detail: { modo: 'registro' } })
                )
              );
            }}
          >
            {t('gateSignUp')}
          </button>
        ) : pagos?.configured ? (
          <button type="button" className="btn acc__wide" onClick={suscribirse} disabled={yendo}>
            {yendo ? t('sending') : t('goPro', { price: pagos.price?.toLocaleString('es-CL') ?? '' })}
          </button>
        ) : (
          // Sin pasarela conectada no se enseña boton: prometer uno que no cobra
          // es peor que no ofrecerlo.
          <p className="muted progate__soon">{t('proSoon')}</p>
        )}

        <button type="button" className="btn btn--ghost acc__wide" onClick={pedirCierre}>
          {t('close')}
        </button>
      </div>
    </div>,
    document.body
  );
}
